import * as _ from 'lodash-es';
import { logs, SeverityNumber, type AnyValueMap } from '@opentelemetry/api-logs';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOG_LEVELS: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

const SEVERITY_MAP: Record<LogLevel, SeverityNumber> = {
  debug: SeverityNumber.DEBUG,
  info: SeverityNumber.INFO,
  warn: SeverityNumber.WARN,
  error: SeverityNumber.ERROR
};

export class Logger {
  private readonly name: string;
  private minLevel: LogLevel = 'info';
  private consoleEnabled = true;
  private otelEnabled = true;

  constructor(name: string) {
    this.name = name;
  }

  configure(options: { level?: LogLevel; console?: boolean; otel?: boolean }): this {
    if (!_.isNil(options.level)) this.minLevel = options.level;
    if (!_.isNil(options.console)) this.consoleEnabled = options.console;
    if (!_.isNil(options.otel)) this.otelEnabled = options.otel;
    return this;
  }

  child(childName: string): Logger {
    const child = new Logger(`${this.name}:${childName}`);
    child.minLevel = this.minLevel;
    child.consoleEnabled = this.consoleEnabled;
    child.otelEnabled = this.otelEnabled;
    return child;
  }

  debug(message: string, attrs?: AnyValueMap): void {
    this.emit('debug', message, attrs);
  }

  info(message: string, attrs?: AnyValueMap): void {
    this.emit('info', message, attrs);
  }

  warn(message: string, attrs?: AnyValueMap): void {
    this.emit('warn', message, attrs);
  }

  error(message: string, attrs?: AnyValueMap): void {
    this.emit('error', message, attrs);
  }

  private emit(level: LogLevel, message: string, attrs: AnyValueMap = {}): void {
    if (LOG_LEVELS[level] < LOG_LEVELS[this.minLevel]) return;

    if (this.consoleEnabled) {
      const fn = level === 'info' ? console.log : console[level];
      if (_.isEmpty(attrs)) {
        fn(message);
      } else {
        fn(message, attrs);
      }
    }

    if (this.otelEnabled) {
      // trace_id/span_id are NOT copied into attributes: the logs SDK stamps them onto
      // the LogRecord's dedicated fields from the active context (see runInPageContext),
      // which is what SigNoz uses for log<->trace correlation. Duplicating them as
      // attributes only bloats the record.
      //
      // Resolved per emit rather than cached in the constructor: unlike the trace API,
      // api-logs has no proxy, so a Logger constructed before instrumentation.ts registers
      // the global provider would capture a permanent no-op logger.
      logs.getLogger(this.name).emit({
        body: message,
        severityNumber: SEVERITY_MAP[level],
        severityText: _.toUpper(level),
        attributes: _.assign({}, attrs, { 'log.namespace': this.name })
      });
    }
  }
}

export const createLogger = (name: string): Logger => new Logger(name);
